import { useEffect, useRef, useState } from 'react';
import { X, Download, Copy, Check, FileText, ExternalLink, Image as ImageIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { VideoScriptDoc } from './VideoScriptDoc';
import { CreativeEditPanel } from './CreativeEditPanel';
import { CreativeLayersPanel } from './CreativeLayersPanel';
import { AngleSetDoc } from './AngleSetDoc';

export interface AdViewData {
  id: string;
  name: string;
  campaignName?: string;
  adSetName?: string;
  status?: string;
  creativeUrl?: string;
  thumbnailUrl?: string;
  videoUrl?: string;
  headline?: string;
  adCopy?: string;
  cta?: string;
  spend?: number;
  impressions?: number;
  reach?: number;
  clicks?: number;
  ctr?: number;
  cpc?: number;
  purchases?: number;
  revenue?: number;
  roas?: number;
}

export type SidebarPanelType = 'report' | 'ad_view' | 'analytics' | 'video_script' | 'creative_edit' | 'creative_layers' | 'angle_set';

export interface AnalyticsPanelData {
  toolName: string;
  analysis: {
    summary: string;
    current: Record<string, number>;
    previous?: Record<string, number>;
    deltas?: Record<string, { value: number; percent: number; direction: "up" | "down" | "flat" }>;
    anomalies: string[];
  };
  renderCard: {
    title: string;
    timeframeLabel: string;
    data: unknown;
  };
  heartbeatLinks: Array<{ chunkId: number; category: string; summary: string }>;
}

export interface SidebarPanelContent {
  type: SidebarPanelType;
  ownerProjectId?: number | null;
  title: string;
  markdown?: string;
  ad?: AdViewData;
  analyticsData?: AnalyticsPanelData;
  /** video_script panels: the video_clone_drafts row to load/edit. */
  draftId?: number;
  /** video_script panels: scene to open expanded (v5.13 tile hover buttons).
   *  null/undefined = keep the doc's default collapsed state (e.g. the final
   *  stitched tile, which belongs to no single scene). Re-calling
   *  openSidebarPanel with a different value re-expands live — the doc
   *  watches the prop. */
  expandScene?: number | null;
  /** angle_set panels: the ad_angle_sets row to load (v6.77 chat Angles doc). */
  angleSetId?: number;
}

interface KPITileProps {
  label: string;
  value: string;
}

function KPITile({ label, value }: KPITileProps) {
  return (
    <div
      style={{
        background: '#F5F3EE',
        border: '1px solid #E5E2DC',
        borderRadius: 10,
        padding: '10px 12px',
      }}
      data-testid={`kpi-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div style={{ fontSize: 10, color: '#B5B0A8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#1A1A1A' }}>
        {value}
      </div>
    </div>
  );
}

function fmt(v?: number): string {
  if (v === undefined || v === null) return '-';
  return v.toLocaleString();
}

function fmtCurrency(v?: number): string {
  if (v === undefined || v === null) return '-';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(v?: number): string {
  if (v === undefined || v === null) return '-';
  return `${v.toFixed(2)}%`;
}

function fmtRoas(v?: number): string {
  if (v === undefined || v === null) return '-';
  return `${v.toFixed(2)}x`;
}

function AdViewSidebarContent({ ad }: { ad: AdViewData }) {
  const statusColors: Record<string, { bg: string; text: string }> = {
    ACTIVE: { bg: '#DCFCE7', text: '#166534' },
    PAUSED: { bg: '#FEF9C3', text: '#713F12' },
    INACTIVE: { bg: '#F3F4F6', text: '#6B7280' },
    DELETED: { bg: '#FEE2E2', text: '#991B1B' },
  };
  const statusKey = (ad.status || '').toUpperCase();
  const statusStyle = statusColors[statusKey] || { bg: '#F5F3EE', text: '#8C8578' };

  const kpis: Array<{ label: string; value: string }> = [
    { label: 'Spend', value: fmtCurrency(ad.spend) },
    { label: 'Impressions', value: fmt(ad.impressions) },
    { label: 'Reach', value: fmt(ad.reach) },
    { label: 'Clicks', value: fmt(ad.clicks) },
    { label: 'CTR', value: fmtPct(ad.ctr) },
    { label: 'CPC', value: fmtCurrency(ad.cpc) },
    { label: 'Purchases', value: fmt(ad.purchases) },
    { label: 'Revenue', value: fmtCurrency(ad.revenue) },
    { label: 'ROAS', value: fmtRoas(ad.roas) },
  ];

  return (
    <div style={{ padding: '20px 24px' }}>
      {/* Status badge */}
      {ad.status && (
        <div style={{ marginBottom: 16 }}>
          <span
            data-testid="ad-view-status-badge"
            style={{
              display: 'inline-block',
              padding: '3px 10px',
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 600,
              background: statusStyle.bg,
              color: statusStyle.text,
              letterSpacing: '0.03em',
            }}
          >
            {ad.status}
          </span>
        </div>
      )}

      {/* Meta info */}
      {(ad.campaignName || ad.adSetName) && (
        <div style={{ marginBottom: 16, fontSize: 12, color: '#8C8578', lineHeight: 1.6 }}>
          {ad.campaignName && (
            <div data-testid="ad-view-campaign">
              <span style={{ fontWeight: 600, color: '#6B6760' }}>Campaign:</span> {ad.campaignName}
            </div>
          )}
          {ad.adSetName && (
            <div data-testid="ad-view-adset">
              <span style={{ fontWeight: 600, color: '#6B6760' }}>Ad Set:</span> {ad.adSetName}
            </div>
          )}
        </div>
      )}

      {/* Creative image/video */}
      {(ad.videoUrl || ad.creativeUrl || ad.thumbnailUrl) ? (
        <div style={{ marginBottom: 20, borderRadius: 12, overflow: 'hidden', border: '1px solid #E5E2DC' }}>
          {ad.videoUrl ? (
            <video
              src={ad.videoUrl}
              poster={ad.thumbnailUrl || undefined}
              controls
              data-testid="ad-view-creative-video"
              style={{ width: '100%', maxHeight: 360, display: 'block', background: '#000' }}
            />
          ) : (
            <img
              src={ad.thumbnailUrl || ad.creativeUrl}
              alt={ad.name}
              data-testid="ad-view-creative"
              style={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
        </div>
      ) : (
        <div
          style={{
            marginBottom: 20,
            borderRadius: 12,
            border: '1px solid #E5E2DC',
            height: 180,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#F5F3EE',
          }}
        >
          <div style={{ textAlign: 'center', color: '#B5B0A8' }}>
            <ImageIcon style={{ width: 32, height: 32, marginBottom: 8 }} />
            <div style={{ fontSize: 12 }}>No creative available</div>
          </div>
        </div>
      )}

      {/* Ad copy / headline / CTA */}
      {(ad.headline || ad.adCopy || ad.cta) && (
        <div
          style={{
            background: '#FAFAF9',
            border: '1px solid #E5E2DC',
            borderRadius: 12,
            padding: '14px 16px',
            marginBottom: 20,
          }}
        >
          {ad.headline && (
            <div
              data-testid="ad-view-headline"
              style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A', marginBottom: ad.adCopy ? 8 : 0 }}
            >
              {ad.headline}
            </div>
          )}
          {ad.adCopy && (
            <div
              data-testid="ad-view-copy"
              style={{ fontSize: 13, color: '#3D3A36', lineHeight: 1.65, marginBottom: ad.cta ? 10 : 0 }}
            >
              {ad.adCopy}
            </div>
          )}
          {ad.cta && (
            <div
              data-testid="ad-view-cta"
              style={{
                display: 'inline-block',
                marginTop: 4,
                padding: '5px 14px',
                borderRadius: 6,
                background: '#1A1A1A',
                color: '#FFFFFF',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {ad.cta}
            </div>
          )}
        </div>
      )}

      {/* KPI Grid — 3 columns, 9 metrics */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#8C8578', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Performance
        </div>
        <div
          data-testid="ad-view-kpi-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 8,
          }}
        >
          {kpis.map(({ label, value }) => (
            <KPITile key={label} label={label} value={value} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ReportMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '24px 0 12px', color: '#111827' }}>{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: '20px 0 8px', color: '#1f2937' }}>{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '16px 0 6px', color: '#374151' }}>{children}</h3>
        ),
        h4: ({ children }) => (
          <h4 style={{ fontSize: 13, fontWeight: 600, margin: '14px 0 5px', color: '#4b5563' }}>{children}</h4>
        ),
        strong: ({ children }) => (
          <strong style={{ fontWeight: 700, color: '#111827' }}>{children}</strong>
        ),
        em: ({ children }) => (
          <em style={{ fontStyle: 'italic' }}>{children}</em>
        ),
        blockquote: ({ children }) => (
          <blockquote style={{ borderLeft: '3px solid #d1d5db', paddingLeft: 12, margin: '8px 0', color: '#6b7280' }}>{children}</blockquote>
        ),
        ul: ({ children }) => (
          <ul style={{ paddingLeft: 20, margin: '6px 0', listStyleType: 'disc' }}>{children}</ul>
        ),
        ol: ({ children }) => (
          <ol style={{ paddingLeft: 20, margin: '6px 0', listStyleType: 'decimal' }}>{children}</ol>
        ),
        li: ({ children }) => (
          <li style={{ margin: '3px 0', paddingLeft: 4, lineHeight: 1.7 }}>{children}</li>
        ),
        code: ({ children }) => (
          <code style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 5px', fontSize: 12, fontFamily: 'monospace', color: '#374151' }}>{children}</code>
        ),
        p: ({ children }) => (
          <p style={{ margin: '6px 0', lineHeight: 1.75 }}>{children}</p>
        ),
        hr: () => (
          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />
        ),
        table: ({ children }) => (
          <div style={{ overflowX: 'auto', margin: '12px 0' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead style={{ background: '#f9fafb' }}>{children}</thead>
        ),
        tbody: ({ children }) => (
          <tbody>{children}</tbody>
        ),
        tr: ({ children }) => (
          <tr style={{ borderBottom: '1px solid #e5e7eb' }}>{children}</tr>
        ),
        th: ({ children }) => (
          <th style={{ padding: '8px 12px', fontWeight: 600, color: '#111827', textAlign: 'left', border: '1px solid #e5e7eb' }}>{children}</th>
        ),
        td: ({ children }) => (
          <td style={{ padding: '8px 12px', color: '#374151', border: '1px solid #e5e7eb', verticalAlign: 'top' }}>{children}</td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function AnalyticsDetailView({ data }: { data: AnalyticsPanelData }) {
  const { analysis, renderCard, heartbeatLinks } = data;
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>{renderCard.timeframeLabel}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginTop: 4 }}>{renderCard.title}</div>
        <div style={{ fontSize: 13, color: "#374151", marginTop: 10 }}>{analysis.summary}</div>
      </div>

      {/* Full KPI block */}
      <div>
        <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>KPIs</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
          {Object.entries(analysis.current).map(([k, v]) => {
            const delta = analysis.deltas?.[k];
            const prevVal = analysis.previous?.[k];
            return (
              <div key={k} style={{ padding: 10, background: "#F9FAFB", borderRadius: 8 }}>
                <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{k}</div>
                <div style={{ fontSize: 20, fontWeight: 600, color: "#111827", marginTop: 2 }}>{typeof v === "number" ? v.toFixed(2) : String(v)}</div>
                {delta && <div style={{ fontSize: 11, color: delta.direction === "up" ? "#16A34A" : delta.direction === "down" ? "#DC2626" : "#6B7280", marginTop: 2 }}>{delta.percent > 0 ? "+" : ""}{delta.percent.toFixed(1)}% vs prev</div>}
                {prevVal !== undefined && <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 1 }}>was {prevVal.toFixed(2)}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {analysis.anomalies.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Flagged</div>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 13, color: "#374151", display: "flex", flexDirection: "column", gap: 4 }}>
            {analysis.anomalies.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      {heartbeatLinks.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>From Brand Heartbeat</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {heartbeatLinks.map((link) => (
              <div key={link.chunkId} style={{ padding: 8, background: "#F9FAFB", borderRadius: 6, fontSize: 12 }}>
                <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>{link.category.replace(/_/g, " ")}</div>
                <div style={{ color: "#374151", marginTop: 2 }}>{link.summary}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface SidebarPanelProps {
  open: boolean;
  content: SidebarPanelContent | null;
  onClose: () => void;
  inline?: boolean;
}

export function SidebarPanel({ open, content, onClose, inline }: SidebarPanelProps) {
  const [copied, setCopied] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === backdropRef.current) onClose();
  };

  const handleCopy = async () => {
    const text = content?.markdown || (content?.ad ? JSON.stringify(content.ad, null, 2) : '');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const handleDownload = () => {
    if (!content) return;
    const text = content.markdown || (content.ad ? JSON.stringify(content.ad, null, 2) : '');
    if (!text) return;
    const ext = content.type === 'report' ? '.md' : '.json';
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(content.title || 'document').replace(/[^a-z0-9]/gi, '_').toLowerCase()}${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Backdrop — hidden in inline mode */}
      {!inline && (
        <div
          ref={backdropRef}
          data-testid="sidebar-panel-backdrop"
          onClick={handleBackdropClick}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 200,
            pointerEvents: open ? 'auto' : 'none',
            opacity: open ? 1 : 0,
            transition: 'opacity 0.25s ease',
          }}
        />
      )}

      {/* Slide-in panel */}
      <div
        data-testid="sidebar-panel"
        style={inline ? {
          position: 'relative',
          width: '100%',
          height: '100%',
          background: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        } : {
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '55%',
          minWidth: 360,
          maxWidth: 820,
          background: '#FFFFFF',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
          zIndex: 201,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.32, 0, 0.15, 1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid #E5E2DC',
            background: '#FFFFFF',
          }}
        >
          {content?.type === 'ad_view' ? (
            <ExternalLink size={15} style={{ color: '#8C8578', flexShrink: 0 }} />
          ) : (
            <FileText size={15} style={{ color: '#8C8578', flexShrink: 0 }} />
          )}
          <span
            data-testid="sidebar-panel-title"
            style={{
              flex: 1,
              fontWeight: 600,
              fontSize: 14,
              color: '#111827',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {content?.title || 'Document'}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {content?.type === 'report' && (
              <>
                <button
                  data-testid="sidebar-panel-copy"
                  onClick={handleCopy}
                  title="Copy content"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '5px 10px',
                    border: '1px solid #E5E2DC',
                    borderRadius: 6,
                    background: '#FFFFFF',
                    cursor: 'pointer',
                    fontSize: 12,
                    color: '#6B7280',
                    fontWeight: 500,
                  }}
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button
                  data-testid="sidebar-panel-download"
                  onClick={handleDownload}
                  title="Download as Markdown"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '5px 10px',
                    border: '1px solid #E5E2DC',
                    borderRadius: 6,
                    background: '#FFFFFF',
                    cursor: 'pointer',
                    fontSize: 12,
                    color: '#6B7280',
                    fontWeight: 500,
                  }}
                >
                  <Download size={12} />
                  Download
                </button>
              </>
            )}
            <button
              data-testid="sidebar-panel-close"
              onClick={onClose}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                border: 'none',
                borderRadius: 6,
                background: 'transparent',
                cursor: 'pointer',
                color: '#9CA3AF',
              }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div
          data-testid="sidebar-panel-body"
          style={{
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {content?.type === 'ad_view' && content.ad ? (
            <AdViewSidebarContent ad={content.ad} />
          ) : content?.type === 'report' && content.markdown ? (
            <div style={{ padding: '24px 28px', lineHeight: 1.7, fontSize: 14, color: '#374151' }}>
              <ReportMarkdown content={content.markdown} />
            </div>
          ) : content?.type === 'analytics' && content.analyticsData ? (
            <AnalyticsDetailView data={content.analyticsData} />
          ) : content?.type === 'video_script' && content.draftId ? (
            <VideoScriptDoc draftId={content.draftId} expandScene={content.expandScene} />
          ) : content?.type === 'creative_edit' ? (
            <CreativeEditPanel />
          ) : content?.type === 'creative_layers' ? (
            <CreativeLayersPanel />
          ) : content?.type === 'angle_set' && content.angleSetId ? (
            <AngleSetDoc key={`${content.ownerProjectId}:${content.angleSetId}`} setId={content.angleSetId} ownerProjectId={content.ownerProjectId} />
          ) : (
            <div style={{ padding: '48px 28px', textAlign: 'center', color: '#B5B0A8', fontSize: 14 }}>
              No content available
            </div>
          )}
        </div>
      </div>
    </>
  );
}
